import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

import AppModal from "../../../components/AppModal";
import WizardStepper, { WizardStepPanel } from "../../../components/WizardStepper";
import QueueCheckboxList from "../../../components/QueueCheckboxList";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { WhatsAppsContext } from "../../../context/WhatsApp/WhatsAppsContext";
import { i18n } from "../../../translate/i18n";
import { HUB_WIZARD_STEPS } from "../connectionWizardSteps";
import HubChannelPicker from "./HubChannelPicker";
import {
	formatConnectionChannelLabel,
	formatHubChannelName,
	getChannelIcon,
} from "../connectionChannelsCatalog";

function formatHubChannelLabel(item) {
	return formatHubChannelName(item);
}

export default function HubChannelLinkModal({ open, onClose, integrationSource = "hub" }) {
	const { reload } = useContext(WhatsAppsContext);
	const [channels, setChannels] = useState([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [wizardStep, setWizardStep] = useState(0);
	const [selectedChannelId, setSelectedChannelId] = useState("");
	const [name, setName] = useState("");
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [queues, setQueues] = useState([]);

	useEffect(() => {
		if (!open) {
			setChannels([]);
			setWizardStep(0);
			setSelectedChannelId("");
			setName("");
			setSelectedQueueIds([]);
			setQueues([]);
			return undefined;
		}

		let cancelled = false;

		const loadChannels = async () => {
			setLoading(true);
			try {
				const [channelsResponse, queuesResponse] = await Promise.all([
					api.get("hub-channel/"),
					api.get("/queue"),
				]);
				if (!cancelled) {
					setChannels(Array.isArray(channelsResponse.data) ? channelsResponse.data : []);
					setQueues(Array.isArray(queuesResponse.data) ? queuesResponse.data : []);
				}
			} catch (err) {
				if (!cancelled) {
					setChannels([]);
					setQueues([]);
					toastError(err);
				}
			} finally {
				if (!cancelled) {
					setLoading(false);
				}
			}
		};

		loadChannels();

		return () => {
			cancelled = true;
		};
	}, [open]);

	const handleChannelChange = (channelId) => {
		setSelectedChannelId(channelId);
		const selected = channels.find((item) => String(item.id) === String(channelId));
		setName(selected ? formatHubChannelLabel(selected) : "");
	};

	const handleWizardNext = () => {
		if (!selectedChannelId) {
			toast.error(i18n.t("connections.hubLinkModal.errors.channelRequired"));
			return;
		}
		setWizardStep(1);
	};

	const handleSave = async () => {
		const selectedChannel = channels.find(
			(item) => String(item.id) === String(selectedChannelId)
		);

		if (!selectedChannel) {
			toast.error(i18n.t("connections.hubLinkModal.errors.channelRequired"));
			return;
		}

		if (!name.trim()) {
			toast.error(i18n.t("connections.hubLinkModal.errors.nameRequired"));
			return;
		}

		setSaving(true);
		try {
			await api.post("/hub-channel/", {
				apikey: selectedChannel.id,
				name: name.trim(),
				channel: selectedChannel.channel,
				queueIds: selectedQueueIds,
				maxAttempts: 3,
				integrationSource,
			});
			await reload();
			toast.success(i18n.t("whatsappModal.success"));
			onClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const selectedChannel = channels.find(
		(item) => String(item.id) === String(selectedChannelId)
	);

	const isLastStep = wizardStep >= HUB_WIZARD_STEPS.length - 1;

	return (
		<AppModal
			open={open}
			onClose={onClose}
			title={i18n.t("connections.hubLinkModal.title")}
			subtitle={i18n.t("connections.wizard.subtitle")}
			maxWidth={580}
			bodyClassName="!pt-2"
			footer={
				<div className="flex w-full flex-wrap items-center gap-2">
					<button
						type="button"
						className="btn btn-sub-gray"
						onClick={onClose}
						disabled={saving}
					>
						{i18n.t("whatsappModal.buttons.cancel")}
					</button>
					{wizardStep > 0 && (
						<button
							type="button"
							className="btn btn-sub-gray"
							onClick={() => setWizardStep(0)}
							disabled={saving}
						>
							{i18n.t("connections.wizard.back")}
						</button>
					)}
					<div className="flex-1" />
					{!isLastStep ? (
						<button
							type="button"
							className="btn btn-primary"
							onClick={handleWizardNext}
							disabled={saving || loading || channels.length === 0}
						>
							{i18n.t("connections.wizard.next")}
						</button>
					) : (
						<button
							type="button"
							className="btn btn-primary"
							onClick={handleSave}
							disabled={saving || loading || channels.length === 0}
						>
							{i18n.t("connections.hubLinkModal.submit")}
						</button>
					)}
				</div>
			}
		>
			{loading ? (
				<div className="app-modal-loading py-10">
					<div className="app-modal-spinner" />
				</div>
			) : channels.length === 0 ? (
				<div className="rounded-lg border border-default-200 bg-default-50 px-4 py-5 text-center dark:border-default-700 dark:bg-default-900">
					<p className="m-0 text-sm font-medium text-default-800 dark:text-default-100">
						{i18n.t("connections.hubLinkModal.emptyTitle")}
					</p>
					<p className="mb-0 mt-2 text-sm text-default-500 dark:text-default-400">
						{i18n.t("connections.hubLinkModal.emptyDescription")}
					</p>
				</div>
			) : (
				<>
					<WizardStepper steps={HUB_WIZARD_STEPS} currentStep={wizardStep} />

					{wizardStep === 0 && (
						<WizardStepPanel
							title={i18n.t("connections.wizard.steps.hubChannel")}
							description={i18n.t("connections.hubLinkModal.subtitle")}
						>
							<p className="mb-3 text-sm text-default-500 dark:text-default-400">
								{i18n.t("connections.hubLinkModal.channelHint")}
							</p>
							<HubChannelPicker
								channels={channels}
								selectedChannelId={selectedChannelId}
								onChange={handleChannelChange}
								disabled={saving}
							/>
						</WizardStepPanel>
					)}

					{wizardStep === 1 && (
						<WizardStepPanel
							title={i18n.t("connections.wizard.steps.hubDetails")}
							description={i18n.t("connections.wizard.stepDescriptions.basic")}
						>
							<div className="space-y-4">
								{selectedChannel && (
									<div className="flex items-center gap-3 rounded-lg border border-default-200 bg-default-50 px-3 py-2.5 dark:border-default-700 dark:bg-default-900">
										<div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-white dark:bg-default-800">
											{getChannelIcon(selectedChannel.channel) ? (
												<img
													src={getChannelIcon(selectedChannel.channel)}
													alt=""
													className="size-6 object-contain"
												/>
											) : null}
										</div>
										<div className="min-w-0">
											<p className="mb-0 text-xs font-medium uppercase tracking-wide text-default-500 dark:text-default-400">
												{i18n.t("connections.hubLinkModal.channelTypeLabel")}
											</p>
											<p className="mb-0 text-sm font-semibold text-default-900 dark:text-default-50">
												{formatConnectionChannelLabel(selectedChannel.channel, i18n.t.bind(i18n))}
												{" · "}
												{formatHubChannelName(selectedChannel)}
											</p>
										</div>
									</div>
								)}
								<div>
									<label
										htmlFor="hub-connection-name"
										className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
									>
										{i18n.t("whatsappModal.form.name")}
									</label>
									<input
										id="hub-connection-name"
										type="text"
										className="form-input w-full"
										value={name}
										onChange={(e) => setName(e.target.value)}
									/>
								</div>

								<QueueCheckboxList
									label={i18n.t("queueSelect.inputLabel")}
									options={queues}
									selectedIds={selectedQueueIds}
									onChange={setSelectedQueueIds}
									emptyText="Nenhum setor disponível."
								/>
							</div>
						</WizardStepPanel>
					)}
				</>
			)}
		</AppModal>
	);
}
